<script lang="ts">
  import type { Snippet } from 'svelte';
  import { isNavigationPending } from '@/stores/navigation';

  let {
    variant = 'primary',
    size = 'md',
    disabled = false,
    loading = false,
    loadingLabel,
    followNavigation,
    fullWidth = false,
    type = 'button',
    href = '',
    onclick,
    children
  }: {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    loading?: boolean;
    loadingLabel?: string;
    followNavigation?: boolean;
    fullWidth?: boolean;
    type?: 'button' | 'submit' | 'reset';
    href?: string;
    onclick?: (event: MouseEvent) => void;
    children?: Snippet;
  } = $props();

  const baseClasses = 'inline-flex items-center justify-center font-semibold rounded-xl cursor-pointer select-none transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

  const variantClasses: Record<string, string> = {
    primary: 'bg-primary text-white hover:bg-primary-600 active:bg-primary-700 focus-visible:ring-primary disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed',
    secondary: 'bg-gray-100 text-gray-800 hover:bg-gray-200 active:bg-gray-300 focus-visible:ring-gray-400 dark:bg-dark-tertiary dark:text-dark-text-high dark:hover:bg-dark-elevated dark:active:bg-dark-border',
    ghost: 'bg-transparent text-primary border border-primary/30 hover:bg-primary-50 hover:border-primary active:bg-primary-100 focus-visible:ring-primary dark:hover:bg-primary/10',
    danger: 'bg-error text-white hover:bg-error-dark active:bg-red-800 focus-visible:ring-error'
  };

  const sizeClasses: Record<string, string> = {
    sm: 'px-3.5 py-2 text-sm gap-1.5',
    md: 'px-5 py-2.5 text-base gap-2',
    lg: 'px-7 py-3.5 text-lg gap-2.5'
  };

  const usesNavigationPending = $derived(followNavigation ?? type === 'submit');
  const isBusy = $derived(disabled || loading || (usesNavigationPending && $isNavigationPending));

  function handleClick(event: MouseEvent) {
    if (isBusy) return;
    onclick?.(event);
  }
</script>

{#if href && !isBusy}
  <a
    {href}
    class="{baseClasses} {variantClasses[variant]} {sizeClasses[size]}"
    class:w-full={fullWidth}
    onclick={handleClick}
  >
    {@render children?.()}
  </a>
{:else}
  <button
    {type}
    class="{baseClasses} {variantClasses[variant]} {sizeClasses[size]}"
    class:w-full={fullWidth}
    class:opacity-50={isBusy}
    class:cursor-not-allowed={isBusy}
    class:cursor-pointer={!isBusy}
    disabled={isBusy}
    onclick={handleClick}
  >
    {#if loading || (usesNavigationPending && $isNavigationPending)}
      <svg class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
    {/if}
    {#if (loading || (usesNavigationPending && $isNavigationPending)) && loadingLabel}
      {loadingLabel}
    {:else}
      {@render children?.()}
    {/if}
  </button>
{/if}
